import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation } from 'react-router-dom';
import { renderMenuIcon } from '../../helper/menuIcons';
import { isMenuItemActive } from '../../helper/menuActive';

const CollapsedMenu = ({ menuData, setShowSidebar }) => {
	const location = useLocation();
	const [hoveredIndex, setHoveredIndex] = useState(null);
	const [submenuPosition, setSubmenuPosition] = useState({ top: 0, left: 0 });
	const hoverTimeoutRef = useRef(null);
	const menuItemRefs = useRef({});

	const handleMenuClick = (item) => {
		if (item.path) {
			setShowSidebar('-left-72');
		}
	};

	const handleMouseEnter = (index) => {
		if (hoverTimeoutRef.current) {
			clearTimeout(hoverTimeoutRef.current);
		}

		if (menuItemRefs.current[index]) {
			const rect = menuItemRefs.current[index].getBoundingClientRect();
			setSubmenuPosition({
				top: rect.top,
				left: rect.right + 8
			});
		}

		setHoveredIndex(index);
	};

	const handleMouseLeave = () => {
		hoverTimeoutRef.current = setTimeout(() => {
			setHoveredIndex(null);
		}, 100);
	};

	useEffect(() => {
		return () => {
			if (hoverTimeoutRef.current) {
				clearTimeout(hoverTimeoutRef.current);
			}
		};
	}, []);

	const hoveredItem = hoveredIndex !== null ? menuData[hoveredIndex] : null;

	return (
		<>
			<div className="flex flex-col items-center gap-0.5">
				{menuData.map((item, index) => {
					const active = isMenuItemActive(item, location.pathname);

					return (
					<div key={index} className="w-full" ref={(el) => (menuItemRefs.current[index] = el)}>
						{item.path ? (
							<NavLink
								to={item.path}
								end={item.path === '/admin/dashboard' || item.path === '/admin'}
								className={`nav-item-link flex min-h-9 items-center justify-center py-2 ${active ? 'nav-item-active' : 'nav-item'}`}
								title={item.name}
								onClick={() => handleMenuClick(item)}
								onMouseEnter={() => handleMouseEnter(index)}
								onMouseLeave={handleMouseLeave}
							>
								<span className="nav-item-icon text-base">
									{renderMenuIcon(item.icon)}
								</span>
							</NavLink>
						) : (
							<div
								className={`nav-item-link flex min-h-9 cursor-pointer items-center justify-center py-2 ${active ? 'nav-item-active' : 'nav-item'}`}
								title={item.name}
								onMouseEnter={() => handleMouseEnter(index)}
								onMouseLeave={handleMouseLeave}
							>
								<span className="nav-item-icon text-base">
									{renderMenuIcon(item.icon)}
								</span>
							</div>
						)}
					</div>
					);
				})}
			</div>

			{hoveredItem?.sub?.length > 0 && hoveredIndex !== null &&
				createPortal(
					<div
						className="fixed z-[9999] w-48 rounded-md border border-hairline bg-canvas p-1.5 dark:border-white/10 dark:bg-tile-1 dark:text-white"
						style={{
							top: `${submenuPosition.top}px`,
							left: `${submenuPosition.left}px`,
							maxWidth: 'calc(100vw - 120px)'
						}}
						onMouseEnter={() => {
							if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
						}}
						onMouseLeave={handleMouseLeave}
					>
						{hoveredItem.sub.map((subItem, subIndex) => (
							<NavLink
								key={subIndex}
								to={subItem.path}
								className={({ isActive }) =>
									`nav-item-link-sub mb-0.5 last:mb-0 ${isActive || isMenuItemActive(subItem, location.pathname) ? 'nav-item-active' : 'nav-item'}`
								}
								onClick={() => setShowSidebar('-left-72')}
								title={subItem.name}
							>
								<div className="flex w-full items-center gap-2.5">
									<span className="nav-item-icon text-sm">
										{renderMenuIcon(subItem.icon)}
									</span>
									<span className="whitespace-nowrap">{subItem.name}</span>
								</div>
							</NavLink>
						))}
					</div>,
					document.body
				)
			}
		</>
	);
};

export default CollapsedMenu;
